import { FieldWrapper } from "@/components/form/FieldWrapper/FieldWrapper";

import styles from "./DateField.module.scss";
import {
	Control,
	Controller,
	RegisterOptions,
	useFormContext,
	useFormState,
} from "react-hook-form";
import { useId, useRef } from "react";
import {
	Button,
	CalendarCell,
	Calendar,
	CalendarGrid,
	CalendarGridHeader,
	CalendarHeaderCell,
	DateInput,
	DatePicker,
	DateSegment,
	Dialog,
	Group,
	Heading,
	Popover,
	CalendarGridBody,
	I18nProvider,
	Label,
	DateValue,
} from "react-aria-components";
import IconCalendar from "@/images/icons/calendar.svg";
import ArrowButton from "@/images/icons/arrow-button.svg";
import clsx from "clsx";
import { isToday } from "date-fns";

export interface DateFieldProps {
	control: Control;
	name: string;
	label: string;
	hideLabel?: boolean;
	className?: string;
	registerOptions?: RegisterOptions;
	slotAfterInput?: React.ReactNode;
	isDateUnavailable?: (date: DateValue) => boolean;
	ariaDescribedBy?: string;
}

export const DateField = ({
	control,
	name,
	label,
	hideLabel,
	className,
	registerOptions,
	slotAfterInput,
	isDateUnavailable,
	ariaDescribedBy,
}: DateFieldProps) => {
	const id = useId();
	const errorId = id + "-error";

	return (
		<I18nProvider locale="en-AU">
			<Controller
				control={control}
				name={name}
				rules={registerOptions}
				render={({ field, fieldState }) => {
					return (
						<DatePicker
							className={styles["date-field"]}
							aria-describedby={ariaDescribedBy}
							value={field.value}
							onChange={field.onChange}
							isDateUnavailable={isDateUnavailable}
							shouldForceLeadingZeros
							isInvalid={fieldState.invalid}
							isDisabled={field.disabled}
						>
							<FieldWrapper
								{...{
									name,
									labelSlot: <Label className={styles["date-field__label"]}>{label}</Label>,
									required: registerOptions?.required as boolean,
									className,
									errorId,
								}}
							>
								<Group
									className={styles["date-field__input"]}
									id={id}
								>
									<DateInput
										ref={field.ref}
										className={styles["date-field__input-inner"]}
									>
										{segment => (
											<DateSegment
												className={styles["date-field__segment"]}
												segment={segment}
											/>
										)}
									</DateInput>
									<Button className={styles["date-field__button"]}>
										<IconCalendar />
									</Button>
								</Group>
								<Popover
									isNonModal
									className={styles["popover"]}
								>
									<Dialog>
										<Calendar
											className={styles["calendar"]}
											isDateUnavailable={isDateUnavailable}
										>
											<div className={styles["calendar__header"]}>
												<Button
													slot="previous"
													className={clsx(
														styles["calendar__button"],
														styles["calendar__button--previous"],
													)}
												>
													<ArrowButton />
												</Button>
												<Heading className={styles["calendar__heading"]} />
												<Button
													slot="next"
													className={clsx(
														styles["calendar__button"],
														styles["calendar__button--next"],
													)}
												>
													<ArrowButton />
												</Button>
											</div>
											<CalendarGrid className={styles["calendar__grid"]}>
												<CalendarGridHeader>
													{day => <CalendarHeaderCell>{day}</CalendarHeaderCell>}
												</CalendarGridHeader>
												<CalendarGridBody>
													{date => (
														<CalendarCell
															className={clsx(
																styles["calendar__cell"],
																isToday(date.toString()) && styles["calendar__cell--today"],
															)}
															date={date}
														/>
													)}
												</CalendarGridBody>
											</CalendarGrid>
										</Calendar>
									</Dialog>
								</Popover>
							</FieldWrapper>
						</DatePicker>
					);
				}}
			/>
			{slotAfterInput}
		</I18nProvider>
	);
};
